import { useMemo } from "react";
import type { EmpiRecordGolden } from "@/shared/types/empi";
import type { ParsedRecord, RecordDuplicates } from "@/features/patient/records/api/dataPipelineApi";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";

const str = (v: unknown): string => (typeof v === "string" ? v : "");
/** YYYY-MM-DD from an ISO value, a US "12/10/2019", or a text date ("Dec 10, 2019");
 *  "" when there's no usable date. (A non-ISO value used to show "Not available".) */
const dateOnly = (v: unknown): string => {
  if (typeof v !== "string") return "";
  const s = v.trim();
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  if (iso) return iso[1];
  const pad = (n: number) => String(n).padStart(2, "0");
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\b/.exec(s);
  if (us) return `${us[3]}-${pad(Number(us[1]))}-${pad(Number(us[2]))}`;
  if (!/[a-z]/i.test(s)) return "";
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};
const firstStr = (...vals: unknown[]): string => {
  for (const v of vals) if (typeof v === "string" && v.trim() !== "") return v;
  return "";
};
const firstDate = (...vals: unknown[]): string => {
  for (const v of vals) {
    const d = dateOnly(v);
    if (d) return d;
  }
  return "";
};
// Epoch-ms key for exact-instant matching (DocumentReference↔DiagnosticReport
// below) — deliberately NOT the same coarse "just the calendar day" grouping
// firstDate/dateOnly use for everything else (Observation↔DiagnosticReport
// linking, display/sort dates), since a same-day-only match there was
// wrongly attaching an unrelated document to a report that has no document
// of its own, whenever more than one DocumentReference happened to land on
// the same day (a real, observed case: three same-day DocumentReferences,
// only one of which was ever actually authored at the report's own exact
// effective timestamp). Date.parse (not a raw string compare) so a
// harmless format difference — offset notation, trailing milliseconds —
// can't defeat a real match.
const instantKey = (...vals: unknown[]): string => {
  for (const v of vals) {
    if (typeof v !== "string") continue;
    const ms = Date.parse(v);
    if (!Number.isNaN(ms)) return String(ms);
  }
  return "";
};

// ── Lab component results ←  Observation ───────────────────────────────────

// "unknown" is a genuinely distinct state from "normal" — a value Epic sent
// with no interpretation AND nothing to compare against a reference range
// (missing value, missing range, or both). Collapsing that into "normal"
// used to make an entirely unassessed lab value look confirmed-normal in
// every downstream filter/stat (Normal/Abnormal filters, "Abnormal Tests"
// counts) — see isAbnormalLabStatus below, which every such consumer must
// use instead of a bare `!== "normal"` check.
export type LabResultStatus = "normal" | "low" | "high" | "critical_low" | "critical_high" | "abnormal" | "unknown";

export interface LabComponentResult {
  id: string;
  /** Which upload/sync it came from, its date and the panel it was reported under (grouping). */
  uploadId?: string | null;
  date?: string;
  panel?: string;
  componentName: string;
  value: string; // "" when unknown
  unit: string; // "" when unknown
  referenceRange: string; // "" when unknown
  status: LabResultStatus;
  /** Module 5: a resolved EMPI cluster's golden record (Observation). */
  golden?: EmpiRecordGolden | null;
}

/** True only for a status that reflects a genuine, known abnormal reading —
 *  "unknown" (never assessed) must never count as abnormal any more than it
 *  should count as normal. */
export function isAbnormalLabStatus(status: LabResultStatus): boolean {
  return status === "low" || status === "high" || status === "critical_low" || status === "critical_high" || status === "abnormal";
}

// Text results read against a text normal range — only the clear pairs
// (decided 2026-10-21): a negative normal ("NEG", "Negative", "NL = NEG",
// "None", "Not detected") vs a positive result ("Positive", "Detected",
// "Trace", "1+", "2+"...). Anything else stays "Not assessed".
const NEGATIVE_RE = /^(?:nl\s*=\s*)?(?:neg(?:ative)?|none(?:\s+seen)?|not\s+detected|non[-\s]?reactive|absent)$/i;
const SURE_POSITIVE_RE = /^(?:pos(?:itive)?|detected|reactive|present|trace|\d\+|\++)$/i;

function qualitativeStatus(value: string, range: string): LabResultStatus | null {
  const v = value.trim();
  const r = range.trim();
  if (!v || !NEGATIVE_RE.test(r)) return null;
  if (NEGATIVE_RE.test(v)) return "normal";
  if (SURE_POSITIVE_RE.test(v)) return "abnormal";
  return null;
}

/** lab | vital | exam | other — the backend's own answer (fields.observation_kind,
 *  from data_pipeline/observation_category.py), so the dashboard, this page and
 *  Complete History all agree and nothing is guessed here. The category
 *  fallback below is only for a response from an older backend. */
export type ObservationKind = "lab" | "vital" | "exam" | "other";
const OBSERVATION_KINDS: readonly ObservationKind[] = ["lab", "vital", "exam", "other"];

// No category (uploads read before it was recorded): the standard vital-sign
// names / LOINC codes are still vitals, never lab results. Whole-name match
// only, so "Weight-based dosing" or "Blood pressure cuff size" never count.
const VITAL_NAME_RE = /^(?:body\s+)?(?:weight|height|temperature|bmi|body\s+mass\s+index|pulse|heart\s+rate|respiratory\s+rate|respirations|blood\s+pressure|bp|systolic\s+blood\s+pressure|diastolic\s+blood\s+pressure|oxygen\s+saturation|spo2|o2\s+saturation)$/i;
const VITAL_LOINC = new Set(["29463-7", "8302-2", "8310-5", "39156-5", "8867-4", "9279-1", "85354-9", "8480-6", "8462-4", "2708-6", "59408-5"]);

export function observationKind(fields: Record<string, unknown>): ObservationKind {
  const fromBackend = str(fields.observation_kind) as ObservationKind;
  if (OBSERVATION_KINDS.includes(fromBackend)) return fromBackend;
  const c = str(fields.category).trim().toLowerCase().replace(/[\s_]+/g, "-");
  if (!c) {
    const name = str(fields.display_name).trim().replace(/[:\s]+$/, "");
    return VITAL_NAME_RE.test(name) || VITAL_LOINC.has(str(fields.loinc_code).trim()) ? "vital" : "lab";
  }
  if (c === "laboratory" || c === "lab" || c === "labs") return "lab";
  if (c.startsWith("vital")) return "vital";
  if (c === "exam" || c === "physical-exam" || c === "physical-examination") return "exam";
  return "other";
}

function computeStatus(f: Record<string, unknown>): LabResultStatus {
  const raw = String(f.interpretation ?? "").trim().toLowerCase();
  if (raw) {
    if (raw.includes("critical") && raw.includes("low")) return "critical_low";
    if (raw.includes("critical") && raw.includes("high")) return "critical_high";
    if (raw === "ll") return "critical_low";
    if (raw === "hh") return "critical_high";
    if (raw === "h" || raw.includes("high")) return "high";
    if (raw === "l" || raw.includes("low")) return "low";
    if (raw === "n" || raw.includes("normal")) return "normal";
  }
  const value = parseFloat(String(f.value_quantity ?? ""));
  const low = parseFloat(String(f.reference_range_low ?? ""));
  const high = parseFloat(String(f.reference_range_high ?? ""));
  const hasRange = !Number.isNaN(low) || !Number.isNaN(high);
  if (!Number.isNaN(value) && hasRange) {
    if (!Number.isNaN(low) && value < low) return "low";
    if (!Number.isNaN(high) && value > high) return "high";
    return "normal"; // a real value, actually measured against a real range, and it's within bounds
  }
  const qualitative = qualitativeStatus(
    firstStr(f.value_string, f.value_codeable_text, Number.isNaN(value) ? f.value_quantity : ""),
    str(f.reference_range_text),
  );
  if (qualitative) return qualitative;
  // No interpretation from the source, and nothing to compare the value
  // against (missing value, missing range, or both) — genuinely unassessed,
  // not confirmed normal.
  return "unknown";
}

function buildRange(f: Record<string, unknown>): string {
  const low = str(f.reference_range_low);
  const high = str(f.reference_range_high);
  if (low || high) return `${low || "?"} - ${high || "?"}`;
  // A qualitative range ("Negative", "See report") has no numeric low/high
  // at all — only this free-text field.
  return str(f.reference_range_text);
}

export function adaptLabComponent(record: ParsedRecord): LabComponentResult {
  const f = record.fields;
  return {
    id: record.id,
    uploadId: record.uploadId ?? null,
    date: firstDate(f.effective_date, f.issued),
    panel: str(f.panel_name),
    componentName: firstStr(f.display_name) || "Lab value",
    value: firstStr(f.value_quantity, f.value_string, f.value_codeable_text),
    unit: str(f.value_unit),
    referenceRange: buildRange(f),
    status: computeStatus(f),
    golden: record.golden ?? null,
  };
}

// ── Lab reports ←  DiagnosticReport (non-imaging) ──────────────────────────

export type LabCategory = "blood" | "urine" | "microbiology" | "pathology" | "genetic" | "other";
export type LabReportStatus = "pending" | "preliminary" | "final" | "corrected" | "cancelled";

export interface LabReportRecord {
  id: string;
  testName: string;
  testCode: string; // "" when unknown
  category: LabCategory;
  collectionDate: string; // "" when unknown
  resultDate: string; // "" when unknown
  orderingProvider: string; // "" when unknown
  performingLab: string; // "" when unknown
  status: LabReportStatus;
  interpretation: string; // "" when unknown
  results: LabComponentResult[];
  criticalFlags: boolean;
  /** Found in several uploads/syncs and shown once (combined view). */
  duplicates?: RecordDuplicates | null;
  /** This DiagnosticReport's own id — its presentedForm attachment (fetched
   *  by epic_integration/service.py::fetch_binary) is the precise source
   *  for FilePane, preferred over documentReferenceId below. */
  diagnosticReportId: string;
  /** A DocumentReference authored at this report's exact effective/issued
   *  instant, if one exists — used only as a fallback when the report
   *  itself has no stored presentedForm file. Matched on the exact instant,
   *  not just the calendar day (a same-day-only match used to attach a
   *  completely unrelated document whenever more than one landed on the
   *  same day). null when no matching document was found. */
  documentReferenceId: string | null;
}

function mapLabCategory(category: string, name: string): LabCategory {
  const hay = `${category} ${name}`.toLowerCase();
  if (hay.includes("blood") || hay.includes("hematolog") || hay.includes("chemistry") || hay.includes("cbc") || hay.includes("panel")) {
    return "blood";
  }
  if (hay.includes("urine") || hay.includes("urinaly")) return "urine";
  if (hay.includes("microbiolog") || hay.includes("culture")) return "microbiology";
  if (hay.includes("patholog") || hay.includes("biopsy") || hay.includes("cytolog")) return "pathology";
  if (hay.includes("genetic") || hay.includes("genom")) return "genetic";
  return "other";
}

function mapReportStatus(status: string): LabReportStatus {
  const s = status.toLowerCase();
  if (s.includes("prelim") || s.includes("partial")) return "preliminary";
  if (s.includes("correct") || s.includes("amend")) return "corrected";
  if (s.includes("cancel") || s.includes("entered-in-error")) return "cancelled";
  if (s.includes("registered") || s.includes("pending")) return "pending";
  return "final";
}

// ── Imaging ─────────────────────────────────────────────────────────────
//
// EHR/data-feed-sourced imaging (DiagnosticReport records categorized as
// imaging) is deliberately NOT surfaced anywhere on this page any more — the
// Imaging tab now shows only patient-uploaded DICOM studies (see
// useImagingStudies.ts / GET /api/patient/imaging-studies), which have real
// pixel data, unlike these EHR reports (Epic/the hospital only ever attached
// the radiologist's report text here, never actual DICOM images — see the
// git history for the "media/imaging Epic-sync code removed" commit this
// followed). isImagingReport is kept only to keep an imaging-categorized
// DiagnosticReport OUT of the Lab Results list below, not to build a second
// imaging list from it.
function isImagingReport(f: Record<string, unknown>): boolean {
  const category = str(f.category).toLowerCase();
  const contentType = str(f.attachment_content_type).toLowerCase();
  const name = str(f.display_name).toLowerCase();
  return (
    category.includes("imag") ||
    category.includes("radiol") ||
    contentType.includes("image") ||
    contentType.includes("dicom") ||
    /x-?ray|mri|ct scan|ultrasound|sonogram|mammogram|radiograph|\bpet\b/.test(name)
  );
}

// ── Which results belong to which report ─────────────────────────────────
//
// Never by date alone (decided 2026-10-21: that put every result of a day —
// physical-exam findings included — into every report of that day):
//   1. the report's own result list (Epic DiagnosticReport.result -> resultIds);
//   2. otherwise the panel each result was reported under (C-CDA organizer /
//      AI-read heading -> panel_name), matched to the report's name, same upload;
//   3. results with a panel but no matching report -> that panel, as its own group;
//   4. anything left -> "Other results" for its day. Never guessed into a report.
// A document's one-line "report" that is really one of its own results (an
// AI read of "ER Troponin") and has no results of its own is not listed.

const panelKey = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const isCritical = (c: LabComponentResult) => c.status === "critical_low" || c.status === "critical_high";

function groupRecord(id: string, testName: string, date: string, results: LabComponentResult[], category: LabCategory): LabReportRecord {
  return {
    id, testName, testCode: "", category, collectionDate: date, resultDate: date,
    orderingProvider: "", performingLab: "", status: "final", interpretation: "",
    results, criticalFlags: results.some(isCritical), diagnosticReportId: "", documentReferenceId: null,
  };
}

export function groupLabResults(
  reports: ParsedRecord[],
  labObservations: LabComponentResult[],
  allById: Map<string, LabComponentResult>,
  documentsByInstant: Map<string, string> = new Map(),
): LabReportRecord[] {
  const used = new Set<string>();
  const take = (c: LabComponentResult) => { used.add(c.id); return c; };
  const out: LabReportRecord[] = [];
  const byPanel: { report: ParsedRecord; target: LabReportRecord; key: string; date: string }[] = [];

  for (const r of reports) {
    const f = r.fields;
    const name = firstStr(f.display_name, f.code) || "Diagnostic report";
    const date = firstDate(f.effective_date, f.issued_date);
    const target: LabReportRecord = {
      id: r.id,
      duplicates: r.duplicates ?? null,
      testName: name,
      testCode: str(f.code),
      category: mapLabCategory(str(f.category), name),
      collectionDate: date,
      resultDate: firstDate(f.issued_date, f.effective_date),
      orderingProvider: firstStr(f.performer_display),
      // Same source field as orderingProvider: shown once, never twice.
      performingLab: "",
      status: mapReportStatus(str(f.status)),
      interpretation: firstStr(f.interpreter_display),
      results: [],
      criticalFlags: false,
      diagnosticReportId: r.id,
      documentReferenceId: documentsByInstant.get(instantKey(f.effective_date, f.issued_date)) || null,
    };
    out.push(target);
    if (r.resultIds) {
      // 1. The report's own list: authoritative, whatever the result's category.
      target.results = r.resultIds.map((id) => allById.get(id)).filter((c): c is LabComponentResult => !!c).map(take);
      target.criticalFlags = target.results.some(isCritical);
    } else {
      byPanel.push({ report: r, target, key: panelKey(name), date });
    }
  }

  // 2. By panel name: same upload, same day (or a day missing on either side).
  for (const { report, target, key, date } of byPanel) {
    target.results = labObservations
      .filter((c) => !used.has(c.id) && !!c.panel && panelKey(c.panel) === key
        && (c.uploadId ?? null) === (report.uploadId ?? null) && (!date || !c.date || c.date === date))
      .map(take);
    target.criticalFlags = target.results.some(isCritical);
  }

  // 3. A panel with no report of its own.
  const panels = new Map<string, LabComponentResult[]>();
  for (const c of labObservations) {
    if (used.has(c.id) || !c.panel) continue;
    const k = `${c.uploadId ?? ""}|${panelKey(c.panel)}|${c.date ?? ""}`;
    panels.set(k, [...(panels.get(k) ?? []), take(c)]);
  }
  for (const [k, results] of panels) {
    const name = results[0].panel || "Lab panel";
    out.push(groupRecord(`panel:${k}`, name, results[0].date ?? "", results, mapLabCategory("", name)));
  }

  // 4. Everything else, by day.
  const days = new Map<string, LabComponentResult[]>();
  for (const c of labObservations) {
    if (used.has(c.id)) continue;
    const day = c.date ?? "";
    days.set(day, [...(days.get(day) ?? []), take(c)]);
  }
  for (const [day, results] of days) {
    out.push(groupRecord(`other:${day}`, "Other results", day, results, "other"));
  }

  // A document's one-line "report" that is really one of its own results.
  const resultNames = new Map<string, Set<string>>();
  for (const c of labObservations) {
    const u = c.uploadId ?? "";
    resultNames.set(u, (resultNames.get(u) ?? new Set<string>()).add(panelKey(c.componentName)));
  }
  const reportById = new Map(reports.map((r) => [r.id, r]));
  return out
    .filter((x) => {
      const report = reportById.get(x.id);
      if (x.results.length > 0 || !report || report.resultIds) return true;
      return !resultNames.get(report.uploadId ?? "")?.has(panelKey(x.testName));
    })
    .sort((a, b) => b.collectionDate.localeCompare(a.collectionDate));
}

export interface DiagnosticsResult {
  labResults: LabReportRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDiagnosticsImaging(): DiagnosticsResult {
  const { records, isLoading, error, refresh } = useParsedRecords();
  return useMemo(() => ({ labResults: buildLabResults(records), isLoading, error, refresh }), [records, isLoading, error, refresh]);
}

/** Every lab report / panel of the patient with its own results — the one
 *  grouping the Diagnostics page and the Health Timeline both show. */
export function buildLabResults(records: ParsedRecord[]): LabReportRecord[] {
  const allObservations = records.filter((r) => r.resourceType === "Observation");
  const byId = new Map(allObservations.map((r) => [r.id, adaptLabComponent(r)]));
  // Only lab results belong on this page: exam findings, vitals and the rest
  // are shown under Complete History -> Exam & Vitals.
  const labObservations = allObservations.filter((r) => observationKind(r.fields) === "lab").map((r) => byId.get(r.id)!);

  // Best-effort link from a lab/imaging report to the actual scanned
  // document (PDF/image) of that report: FHIR doesn't guarantee a direct
  // reference between DiagnosticReport and DocumentReference, so this
  // matches on the exact authored/effective INSTANT, not just the
  // calendar day — a same-day-only match was wrongly attaching an
  // unrelated document (a different report entirely) whenever more than
  // one DocumentReference landed on the same day, which real patient data
  // routinely does. First document seen for a given instant wins.
  const documentsByInstant = new Map<string, string>();
  for (const r of records) {
    if (r.resourceType !== "DocumentReference") continue;
    const key = instantKey(r.fields.authored_date);
    if (!key || documentsByInstant.has(key)) continue;
    documentsByInstant.set(key, r.id);
  }

  const reports = records.filter((r) => r.resourceType === "DiagnosticReport" && !isImagingReport(r.fields));
  return groupLabResults(reports, labObservations, byId, documentsByInstant);
}
